import { useQuery } from '@apollo/client';
import { useRouter } from '@application/utils';
import { graphql } from '@gql/gql';
import type { StudListGetStudsQuery, StudListGetStudsQueryVariables } from '@gql/graphql';
import { useCallback, useState } from 'react';
import { PAGINATED_SIZE } from '../constants';
import { useSearch } from '../useSearch';
import { useStudOrderBuilder } from './useStudOrderBuilder';
import { useStudWhereBuilder } from './useStudWhereBuilder';

export const STUD_LIST_GET_STUDS = graphql(`
  query StudListGetStuds(
    $where: AnimalWhereInput
    $breedingCardInfoTranslationsWhereBreed: DogBreedTranslationWhereInput
    $take: Int
    $skip: Int
    $orderBy: [AnimalOrderByWithRelationAndSearchRelevanceInput!]
  ) {
    findManyAnimal(where: $where, take: $take, skip: $skip, orderBy: $orderBy) {
      id
      name
      imgs(take: 1) {
        id
        small
      }
      dogInfos {
        id
        pedigree
        dogBreed {
          translations(where: $breedingCardInfoTranslationsWhereBreed) {
            id
            name
          }
        }
      }
      studInfos {
        id
        price
        champion
      }
      owner {
        address {
          region
          country
        }
      }
    }

    aggregateAnimal(where: $where) {
      _count {
        _all
      }
    }
  }
`);

export const useGetStuds = () => {
  const { language } = useRouter();
  const [studsFetchMoreLoading, setStudsFetchMoreLoading] = useState(false);
  const { valuesFromSearch } = useSearch();
  const { studWhereBuilder } = useStudWhereBuilder();
  const { studOrderBuilder } = useStudOrderBuilder();

  const { data, error, loading, fetchMore, refetch } = useQuery<
    StudListGetStudsQuery,
    StudListGetStudsQueryVariables
  >(STUD_LIST_GET_STUDS, {
    variables: {
      take: PAGINATED_SIZE,
      skip: 0,
      where: studWhereBuilder(valuesFromSearch),
      breedingCardInfoTranslationsWhereBreed: { language: { equals: language } },
      orderBy: studOrderBuilder(),
    },
  });

  const studsFetchMore = useCallback(
    async (...args: Parameters<typeof fetchMore>) => {
      setStudsFetchMoreLoading(true);
      try {
        await fetchMore(...args);
      } finally {
        setStudsFetchMoreLoading(false);
      }
    },
    [fetchMore],
  );

  return {
    studs: data?.findManyAnimal,
    // eslint-disable-next-line no-underscore-dangle
    studsCount: data?.aggregateAnimal?._count?._all ?? 0,
    studsError: error,
    studsLoading: loading,
    studsRefetch: refetch,
    studsFetchMore,
    studsFetchMoreLoading,
  };
};
